import React, {useEffect, useMemo, useState} from 'react';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import { useMetaTags } from '../../services/useMetaTags';
import NotFoundPage from '../Errors/NotFound';
import {parseRouteResponse} from '../../services/route-response';

function toPdfFileName(downloadFileName, id) {
    const fallback = `certificate-${id}.pdf`;
    if (!downloadFileName) {
        return fallback;
    }

    if (/\.html?$/i.test(downloadFileName)) {
        return downloadFileName.replace(/\.html?$/i, '.pdf');
    }

    return /\.pdf$/i.test(downloadFileName) ? downloadFileName : `${downloadFileName}.pdf`;
}

function parseFileNameFromContentDisposition(contentDisposition) {
    if (!contentDisposition) {
        return '';
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match && utf8Match[1]) {
        try {
            return decodeURIComponent(utf8Match[1]);
        } catch {
            return utf8Match[1];
        }
    }

    const fallbackMatch = contentDisposition.match(/filename="?([^";]+)"?/i);

    return fallbackMatch && fallbackMatch[1] ? fallbackMatch[1] : '';
}

function resolveLocalePrefix(pathname) {
    const localeMatch = pathname.match(/^\/(en|uk)(?=\/|$)/);
    return localeMatch ? `/${localeMatch[1]}` : '';
}

function withLocalePrefix(path, localePrefix) {
    if (!path) {
        return localePrefix || '/';
    }

    if (!localePrefix || /^https?:\/\//.test(path)) {
        return path;
    }

    if (path === localePrefix || path.startsWith(`${localePrefix}/`)) {
        return path;
    }

    return `${localePrefix}${path.startsWith('/') ? path : `/${path}`}`;
}

export default function TrainingCourseCertificatePage() {
    const {id} = useParams();
    const history = useHistory();
    const location = useLocation();
    const {t} = useTranslation();
    const localePrefix = useMemo(() => resolveLocalePrefix(location.pathname), [location.pathname]);
    const [payload, setPayload] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDownloadPending, setIsDownloadPending] = useState(false);
    const [notFound, setNotFound] = useState(false);
    const certificateTitle = t('courses.certificate.title', 'View certificate');

    useMetaTags({
        title: certificateTitle,
        description: t('app.description'),
        metas: [{name: 'keywords', content: t('app.keywords')}],
    });

    useEffect(() => {
        setIsLoading(true);
        setNotFound(false);

        fetch(`/api/v1/course/${id}/certificate`, {credentials: 'same-origin'})
            .then(async (response) => {
                let json = {};
                try {
                    json = await response.json();
                } catch {
                    json = {};
                }

                return {response, json};
            })
            .then(({response, json}) => {
                const {kind, redirectUrl} = parseRouteResponse(response, json);

                if (kind === 'not_found') {
                    setPayload(null);
                    setNotFound(true);
                    return;
                }

                if (json?.state === 'active') {
                    setPayload(json);
                    return;
                }

                if (kind === 'redirect' || kind === 'unauthorized') {
                    const fallbackRedirect = '/finished-courses';
                    history.replace(withLocalePrefix(redirectUrl || fallbackRedirect, localePrefix));
                    return;
                }

                if (kind === 'error') {
                    throw new Error('Failed to load certificate');
                }

                throw new Error('Unexpected certificate payload');
            })
            .catch(() => {
                history.replace(withLocalePrefix('/500', localePrefix));
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [history, id, localePrefix]);

    const downloadHtmlFallback = () => {
        const html = payload?.certificateHtml;
        if (!html) {
            return;
        }

        const blob = new Blob([html], {type: 'text/html;charset=utf-8'});
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = objectUrl;
        link.download = payload?.downloadFileName || `certificate-${id}.html`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
    };

    const onDownload = async () => {
        if (isDownloadPending) {
            return;
        }

        setIsDownloadPending(true);

        try {
            const pdfUrl = payload?.links?.downloadPdfUrl || `/api/v1/course/${id}/certificate/pdf`;
            const response = await fetch(pdfUrl, {
                method: 'GET',
                credentials: 'same-origin',
            });

            const contentType = (response.headers.get('Content-Type') || '').toLowerCase();
            if (response.ok && contentType.includes('application/pdf')) {
                const blob = await response.blob();
                const objectUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                const serverFileName = parseFileNameFromContentDisposition(response.headers.get('Content-Disposition') || '');

                link.href = objectUrl;
                link.download = serverFileName || toPdfFileName(payload?.downloadFileName, id);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(objectUrl);

                return;
            }

            const json = await response.json().catch(() => ({}));
            const {kind, redirectUrl} = parseRouteResponse(response, json);

            if (kind === 'not_found') {
                setPayload(null);
                setNotFound(true);
                return;
            }

            if (kind === 'redirect' || kind === 'unauthorized') {
                const fallbackRedirect = '/finished-courses';
                history.replace(withLocalePrefix(redirectUrl || fallbackRedirect, localePrefix));
                return;
            }

            throw new Error('Failed to download PDF certificate');
        } catch {
            downloadHtmlFallback();
        } finally {
            setIsDownloadPending(false);
        }
    };

    const backUrl = withLocalePrefix(payload?.links?.backUrl || '/finished-courses', localePrefix);

    if (isLoading) {
        return null;
    }

    if (notFound) {
        return <NotFoundPage />;
    }

    if (!payload) {
        return null;
    }

    return (
        <section id="course-certificate" className="spa-course-certificate">
            <div className="container">
                <div className="portfolio-header flex-center" style={{marginBottom: '16px'}}>
                    <h1>{payload.title || certificateTitle}</h1>
                </div>

                <div style={{display: 'flex', gap: '12px', marginBottom: '16px'}}>
                    <button type="button" className="btn" onClick={onDownload} disabled={isDownloadPending}>
                        {t('courses.certificate.download', 'Download certificate')}
                    </button>
                    <button type="button" className="btn" onClick={() => history.push(backUrl)}>
                        {t('app.back', 'Back')}
                    </button>
                </div>

                <iframe
                    title="course-certificate"
                    srcDoc={payload.certificateHtml || ''}
                    style={{
                        width: '100%',
                        minHeight: '80vh',
                        border: '1px solid #d9dee4',
                        backgroundColor: '#fff',
                    }}
                />
            </div>
        </section>
    );
}
